import React, { useEffect, useMemo, useRef } from 'react';
import { Route, withRouter } from 'react-router-dom';
import { Icon, LinearProgress } from '@mui/material';
import { NavBarContent } from 'feature/nav/NavBar';
import CourseView from '../CourseView';
import { StyledContent, StyledHeaderButton } from './styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { get } from 'global/lib/lodash';
import { Row } from 'global/components/row';
import { sortDocs, canContinue, COURSE_DOCUMENT_SCROLL_ID } from '../courseShared';
import { useScreen } from 'global/styles';
import CopyrightNotice from 'global/components/PageFooter';

const include = ['edition', 'edition.documents', 'edition.documents.reads'].join(',');
const edInclude = ['document'].join(',');

export { canContinue } from '../courseShared';

export const CourseDocument = withRouter((props) => {
  const { match } = props;
  const scrollRef = useRef(null);

  const { isMobile } = useScreen();

  const { id, document } = get(match, 'params', {});

  const locationProps = get(props, 'location.state', {});

  const { data, loading, update } = useRetrieveData(
    { url: `/course-assignments/${id}` },
    {
      useEffect: true,
      build: ['courseAssignments'],
      params: { include },
      transformData: (d) => get(d, '0', {}),
    }
  );

  const { data: edoc, loading: documentLoading, update: documentUpdate } = useRetrieveData(
    { url: `/edition-documents/${document}` },
    {
      useEffect: true,
      build: ['editionDocuments'],
      params: { include: edInclude },
      transformData: (d) => get(d, '0', {}),
    }
  );

  const documents = sortDocs(get(data, 'edition.documents', []));
  const reads = documents.find((d) => d.id === document)?.reads;

  const readState = !!reads ? (!!get(reads, '0.completedAt') ? 2 : 1) : 0;
  const ind = edoc ? documents.findIndex((d) => d.id === edoc.id) : -1;

  const docForView = useMemo(() => {
    if (!edoc) return edoc;
    return { ...edoc, reads: reads };
  }, [edoc, reads]);

  const docIndex = {
    current: ind,
    length: documents.length,
    canNext: canContinue(docForView, locationProps) && ind < documents.length - 1,
  };

  // Reset the document scrollport when the lesson changes (Prev/Next or deep link).
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [document]);

  const updateAll = async () => {
    await Promise.all([window.CourseEditionUpdate && window.CourseEditionUpdate(), update()]);
  };

  const setDoc = (dir = 1) => {
    const nextDoc = documents[ind + dir] || documents[dir > 0 ? 0 : documents.length - 1];

    props.history.push(`/courses/${id}/document/${nextDoc.id}`, locationProps);
  };

  const pageTitle = () => {
    const isAssessment = get(edoc, 'documentableType') === 'assessment';
    return loading ? (
      'Loading...'
    ) : (
      <Row fullWidth>
        {isAssessment ? 'Assessment' : get(edoc, 'document.name', '')}
        <Row style={{ marginLeft: 'auto' }}>
          <StyledHeaderButton isMobile={isMobile} disabled={docIndex.current === 0} onClick={() => setDoc(-1)} color="inherit" startIcon={<Icon>keyboard_arrow_up</Icon>}>
            <span className="btnText">Prev</span>
          </StyledHeaderButton>
          <StyledHeaderButton isMobile={isMobile} disabled={!docIndex.canNext} onClick={() => setDoc(1)} color="inherit" startIcon={<Icon>keyboard_arrow_down</Icon>}>
            <span className="btnText">Next</span>
          </StyledHeaderButton>
        </Row>
      </Row>
    );
  };

  return (
    <StyledContent ref={scrollRef} id={COURSE_DOCUMENT_SCROLL_ID}>
      <NavBarContent pageTitle={pageTitle()} link={{ pathname: '/courses/' + match.params.id, state: locationProps }} />
      {loading && <LinearProgress />}
      <Route
        exact
        path={`/courses/:id/document/:document`}
        render={(routeProps) => (
          <CourseView
            {...routeProps}
            docIndex={docIndex}
            loading={loading}
            documents={data}
            document={docForView}
            update={updateAll}
            setDoc={setDoc}
            footer={<CopyrightNotice ownerRealmId={get(data, 'edition.ownerRealmId')} />}
          />
        )}
      />
    </StyledContent>
  );
});
